/**
 * BeneficiaryEditorModal — nested-modal beneficiary editor for the
 * Add-to-Cart flow (Wave 2c, Bill 2026-05-06; PCB-brand overhaul 2026-06-26).
 *
 * Opens from the AddToCartModal's BENEFICIARIES section when an FSR clicks
 * a "FROM CART" chip (auto-fill from cart person) OR clicks "+ Primary
 * Beneficiary" / "+ Contingent Beneficiary" (blank entry).
 *
 * Mirrors SPFx BeneficiaryPicker.tsx (lines 65-426):
 *   - Primary vs Contingent buckets, switch via inline link per row
 *   - Equal Split All for primary tier (33.33 / 33.33 / 33.34 sums to 100)
 *   - Custodian-required banner when DOB indicates age < 18
 *   - autoAllocate() preserves SPFx 99-115 floor/round semantics
 *
 * Custodian flow per investigation 2026-05-06:
 *   custodianName is captured into `BeneficiaryEntry.custodianName` and
 *   merged into IRA disclosure templates at compile time. It does NOT
 *   flow to Core (no JHA wire field exists). Free-text — no Person picker
 *   since the custodian may not be a banked relation.
 *
 * Layout (Bill 2026-06-26): the original markup shipped without a stylesheet
 * and rendered as a raw HTML form. Re-skinned to the locked 2-col modal
 * pattern (`feedback_modal_two_column_with_guidance_panel_20260508`): editable
 * tiers on the left, a persistent guidance <aside> on the right surfacing the
 * split rule, live allocation state, the minor-custodian rule, and the Core
 * wire mechanism. No behavior change — same drafts, mutators, and validation.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import './BeneficiaryEditorModal.css';
import { ModalShell, Button, Banner, DateTextInput } from '@/components/primitives';
import type { BeneficiaryEntry, CustomerSearchRow } from '@/components/primitives';
import { useConfig } from '@/contexts/ConfigContext';
import { readTestIdentitySettings, testIdentityViolation } from '@/utils/testIdentity';
import { useApiClient } from '@/contexts/ApiClientContext';
import { runCustomerSearch, filterByNameAndDob } from '../hooks/usePersonSearch';
import { formatSsn } from '@/utils/ssn';
import { parseCustInq } from '@/utils/custInqParser';
import { splitFirstMiddle } from '../utils/splitFirstMiddle';
import { useProfileChange, type ProfileChangeNotice } from '@/components/customer/useProfileChange';
import { readProfileChangeMode } from '@/components/customer/profileChange';
import {
  applyCoreOutcome,
  BENEF_CORE_LOCKED_NOTE,
  benefCoreLocked,
  benefNeedsCoreCheck,
  benefToProfilePerson,
  linkSnapshot,
  pickPhoneSlot,
  type BenefCoreProfile,
  type BenefPhoneSlot,
} from './beneficiaryCoreEdit';
import {
  BENEFICIARY_SSN_REQUIREMENT_KEY,
  BENEFICIARY_PHONE_REQUIREMENT_KEY,
  BENEFICIARY_DOB_REQUIREMENT_KEY,
  BENEFICIARY_RELATIONSHIP_REQUIREMENT_KEY,
  DEFAULT_BENEFICIARY_SSN_REQUIREMENT,
  DEFAULT_BENEFICIARY_PHONE_REQUIREMENT,
  DEFAULT_BENEFICIARY_DOB_REQUIREMENT,
  DEFAULT_BENEFICIARY_RELATIONSHIP_REQUIREMENT,
  readBeneficiaryFieldRequirement,
  benefDobSatisfied,
  benefIsEntity,
  benefRelationshipSatisfied,
  type BeneficiaryFieldRequirement,
} from './beneficiaryRequirements';

// ── Beneficiary row (UI state only — committed via onChange) ──────────

export type BeneficiaryRoleBucket = 'primary' | 'contingent';

export interface BenefDraft extends BeneficiaryEntry {
  /**
   * The identity key (see benefIdentityKey) that a completed Core Director
   * search returned NO match for — or that the FSR explicitly dismissed a
   * match on. `noSsn` is only honoured while this equals the row's CURRENT
   * key, so editing the name or DOB after the search silently revokes it.
   *
   * Why on the draft and not in component state: the one-CIF-per-person
   * rule (Bill 2026-09-23) has to hold at Save, not just in a disabled
   * checkbox. A synthetic CIF minted for someone who already has one merges
   * two people onto one record; the search-first gate is the control.
   */
  noSsnClearedKey?: string;
  // NOTE (Bill 2026-07-10, BACKLOG-0847): `cifId?: string` is INHERITED from
  // BeneficiaryEntry. The per-row "Find existing customer" lookup + the
  // automatic match detector pin it when the FSR links an on-file Core
  // Director customer; the commit path (CartCommitService.CreatePodCifsAsync)
  // then SKIPS the CustAdd and wires the existing CIF directly into the POD
  // CustRelAdd BP/BC (and TaxPlnBenfAdd `cif` for IRA).
  /**
   * CHG-0186 (Bill 2026-11-04, option B) — a linked existing customer's Core-held
   * fields (name, DOB, phone) as Core held them, whether the officer changed them,
   * and whether Change in Contact already wrote the change. See beneficiaryCoreEdit.
   */
  coreProfile?: BenefCoreProfile;
  /** Stable UI-only id; persists across rerenders. */
  id: string;
  /** Local display flag — drives % validation bucket. */
  role: BeneficiaryRoleBucket;
  // custodianName / custodianAddress / custodianPhone / custodianEmail and
  // the successor custodian fields are INHERITED from BeneficiaryEntry
  // (Bill 2026-09-30, BACKLOG-1648). Name, address and phone are required
  // for a minor beneficiary — see custodianComplete().
}

// ── Admin-configurable field requirements (Bill 2026-07-10, BACKLOG-0863) ──
// SSN / phone / DOB / relationship capture levels (required | recommended |
// optional) live in ./beneficiaryRequirements (pure, unit-tested). DOB and
// relationship added by seed 414 (Collette Cruse life-ring report 2026-10-16;
// Bill approved 2026-10-16). Rationale for SSN: the beneficiary SSN feeds the
// POD beneficiary CIF create at commit (CartCommitService.CreatePodCifsAsync);
// legacy online-app imports lack it, so the FSR asks the customer and fills it in.
export {
  BENEFICIARY_SSN_REQUIREMENT_KEY,
  BENEFICIARY_PHONE_REQUIREMENT_KEY,
  BENEFICIARY_DOB_REQUIREMENT_KEY,
  BENEFICIARY_RELATIONSHIP_REQUIREMENT_KEY,
  DEFAULT_BENEFICIARY_SSN_REQUIREMENT,
  DEFAULT_BENEFICIARY_PHONE_REQUIREMENT,
  DEFAULT_BENEFICIARY_DOB_REQUIREMENT,
  DEFAULT_BENEFICIARY_RELATIONSHIP_REQUIREMENT,
  readBeneficiaryFieldRequirement,
  benefDobSatisfied,
  benefRelationshipSatisfied,
  benefIsEntity,
} from './beneficiaryRequirements';
export type { BeneficiaryFieldRequirement } from './beneficiaryRequirements';

/**
 * The identity a Core Director search runs on: `ssn:<9 digits>` when a full
 * SSN is present and not waived, else `nd:<name>|<dob digits>` when name and
 * DOB are complete. Empty string when neither is searchable.
 *
 * ONE definition, deliberately. The detector, `unlink` and the no-SSN gate
 * all previously assembled this by hand, which is the same three-copies
 * shape that let the FraudPoint bands and the call-class map drift.
 */
export function benefIdentityKey(b: Pick<BenefDraft, 'taxId' | 'dob' | 'firstName' | 'lastName' | 'noSsn'>): string {
  const ssn = (b.taxId ?? '').replace(/\D/g, '');
  const dobDigits = (b.dob ?? '').replace(/\D/g, '');
  const nm = `${b.firstName ?? ''} ${b.lastName ?? ''}`.trim();
  const useSsn = b.noSsn !== true && ssn.length === 9;
  if (useSsn) return `ssn:${ssn}`;
  const useNameDob = (b.noSsn === true || ssn.length === 0)
    && !!b.firstName && !!b.lastName && dobDigits.length >= 8;
  return useNameDob ? `nd:${nm.toLowerCase()}|${dobDigits}` : '';
}

/** True when `noSsn` may be relied on: Core was searched for THIS identity
 *  and found nobody (or the FSR dismissed the match as a different person). */
export function benefNoSsnCleared(b: BenefDraft): boolean {
  const key = benefIdentityKey(b);
  return key.length > 0 && b.noSsnClearedKey === key;
}

/** True when the draft satisfies the SSN capture rule: linked to an existing
 *  CIF (Bill 2026-07-10, BACKLOG-0847 — the commit path skips CustAdd entirely
 *  for a linked customer, so no SSN entry is needed; the on-file record already
 *  carries it), OR has a taxId, OR the FSR checked "Beneficiary has no
 *  SSN/TIN/ATIN" (noSsn waives it — the commit path CustAdds with Field 92
 *  TINCode='N' instead). Tolerant of legacy drafts that predate these fields. */
export function benefSsnSatisfied(b: BenefDraft): boolean {
  return (b.cifId ?? '').trim().length > 0
    // noSsn alone is no longer enough: Core must have been searched for this
    // exact identity first (Bill 2026-09-23, one CIF per person). A ticked
    // box whose search never completed, errored, or predates a name/DOB edit
    // does not satisfy the rule — the row blocks at Save with the SSN message.
    || (b.noSsn === true && benefNoSsnCleared(b))
    || (b.taxId ?? '').trim().length > 0;
}

/** True when the draft carries a phone number (digits captured). A linked
 *  existing CIF also satisfies (BACKLOG-0847) — phone's only destination is
 *  the CustAdd this row now skips; Core already has the on-file phone. */
export function benefPhoneSatisfied(b: BenefDraft): boolean {
  return (b.cifId ?? '').trim().length > 0 || (b.phone ?? '').trim().length > 0;
}

/** Display name for validation messages ("New beneficiary" for a blank row). */
function benefDisplayName(b: BenefDraft): string {
  const n = `${b.firstName ?? ''} ${b.lastName ?? ''}`.trim();
  return n.length > 0 ? n : 'New beneficiary';
}

/** Calc age helper — duplicates AddToCartModal's so this file is self-contained. */
function calcAge(dob?: string): number {
  if (!dob || dob.length < 10) return -1;
  const [yStr, mStr, dStr] = dob.split('-');
  const y = parseInt(yStr ?? '', 10);
  const m = parseInt(mStr ?? '', 10);
  const d = parseInt(dStr ?? '', 10);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return -1;
  const now = new Date();
  let age = now.getFullYear() - y;
  const monthDiff = (now.getMonth() + 1) - m;
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < d)) age--;
  return age;
}

/** True when the beneficiary's DOB indicates they're under 18. */
export function isMinorBeneficiary(b: BenefDraft): boolean {
  const age = calcAge(b.dob);
  return age >= 0 && age < 18;
}

/**
 * Bill 2026-09-30 (BACKLOG-1648): a minor beneficiary needs a custodian with
 * name, address and phone (email optional; successor custodian optional) —
 * the PCB POD forms print all three and the custodian is who the bank pays.
 */
export function custodianComplete(b: BenefDraft): boolean {
  if (!isMinorBeneficiary(b)) return true;
  return (b.custodianName ?? '').trim().length > 0
    && (b.custodianAddress ?? '').trim().length > 0
    && (b.custodianPhone ?? '').replace(/\D/g, '').length >= 10;
}

// ── autoAllocate — Equal Split All ────────────────────────────────────
// Mirrors SPFx BeneficiaryPicker autoAllocate (lines 99-115). For N
// beneficiaries in a tier, allocates floor(100/N * 100) / 100 to each
// EXCEPT the last, which absorbs the rounding remainder so the sum is
// exactly 100.

export function autoAllocate(
  drafts: ReadonlyArray<BenefDraft>,
  bucket: BeneficiaryRoleBucket,
): BenefDraft[] {
  const others = drafts.filter((b) => b.role !== bucket);
  const tier = drafts.filter((b) => b.role === bucket);
  if (tier.length === 0) return [...others];
  const n = tier.length;
  const eachRaw = 100 / n;
  const each = Math.floor(eachRaw * 100) / 100; // truncate to 2 decimals
  const allocated = each * (n - 1);
  const lastPct = Math.round((100 - allocated) * 100) / 100;
  const adjusted = tier.map((b, i) => ({
    ...b,
    percentage: i === n - 1 ? lastPct : each,
  }));
  return [...others, ...adjusted];
}

// ── Stable id generator (UI-only) ──────────────────────────────────────

function genId(): string {
  return `b_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

// ── Component ──────────────────────────────────────────────────────────

export interface BeneficiaryEditorModalProps {
  open: boolean;
  /** Title shown on the modal — varies by acct type ("POD beneficiaries", "IRA beneficiaries", etc.). */
  title?: string;
  /** Existing drafts (from cart.beneficiariesJson hydration). */
  initial: ReadonlyArray<BenefDraft>;
  /** Cart-person quick-add chips. Filtered by parent already (excludes primary + joints). */
  cartPersonChips?: ReadonlyArray<{
    id: string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    cifId?: string;
    dob?: string;
    taxId?: string;
    phone?: string;
  }>;
  /**
   * Beneficiaries already entered on OTHER product lines (deduped by parent) —
   * offered as one-click reuse chips so the same beneficiary + custodian isn't
   * re-typed per product (Bill v0.70.0 walk / BACKLOG-0837).
   */
  reusableBeneficiaries?: ReadonlyArray<BenefDraft>;
  /**
   * Account type — controls which mechanism the cart commits use.
   * '60' = IRA (TaxPlnBenfAdd). '10'/'20'/'30' = DDA/SAV/CD POD (CustRelAdd BP/BC).
   */
  acctType: '10' | '20' | '30' | '60';
  /**
   * CHG-0186 (Bill 2026-11-04, option B): the application these beneficiaries
   * belong to. When set, Save runs the Change in Contact step (the owner path's
   * useProfileChange) for every linked existing customer whose name, date of
   * birth or phone the officer changed. Omitted = no Core step (maintenance).
   */
  changeInContact?: {
    cartId: string;
    /** CHG-0186: "Authorized by" prefill — the first customer on an officer-created in-branch application. */
    authorizedByPrefill?: string;
  };
  onClose: () => void;
  /** `notices` = the Change in Contact results, one per linked beneficiary that went through it. */
  onCommit: (drafts: ReadonlyArray<BenefDraft>, notices?: ReadonlyArray<ProfileChangeNotice>) => void;
}

export function BeneficiaryEditorModal(
  props: BeneficiaryEditorModalProps,
): JSX.Element | null {
  const { open, title, initial, cartPersonChips, reusableBeneficiaries, acctType, changeInContact, onClose, onCommit } = props;

  const [drafts, setDrafts] = useState<ReadonlyArray<BenefDraft>>(initial);
  const client = useApiClient();
  // CHG-0186 — the owner path's Change in Contact step, reused as-is.
  const { begin: beginProfileChange, modal: profileChangeModal, comparing: comparingProfile } = useProfileChange();
  const [savingCore, setSavingCore] = useState<boolean>(false);
  const [coreNotice, setCoreNotice] = useState<ProfileChangeNotice | null>(null);

  // Admin-configurable SSN/phone requirement levels (Bill 2026-07-10,
  // BACKLOG-0863). Read via the tolerant systemSettings probe; defaults
  // ssn='required', phone='recommended' when the seed hasn't landed.
  const { config } = useConfig();
  const ssnRequirement = useMemo(
    () => readBeneficiaryFieldRequirement(
      config.systemSettings, BENEFICIARY_SSN_REQUIREMENT_KEY, DEFAULT_BENEFICIARY_SSN_REQUIREMENT),
    [config.systemSettings],
  );
  const phoneRequirement = useMemo(
    () => readBeneficiaryFieldRequirement(
      config.systemSettings, BENEFICIARY_PHONE_REQUIREMENT_KEY, DEFAULT_BENEFICIARY_PHONE_REQUIREMENT),
    [config.systemSettings],
  );
  // DOB + relationship (seed 414 — Collette Cruse 2026-10-16; Bill approved).
  const dobRequirement = useMemo(
    () => readBeneficiaryFieldRequirement(
      config.systemSettings, BENEFICIARY_DOB_REQUIREMENT_KEY, DEFAULT_BENEFICIARY_DOB_REQUIREMENT),
    [config.systemSettings],
  );
  const relationshipRequirement = useMemo(
    () => readBeneficiaryFieldRequirement(
      config.systemSettings, BENEFICIARY_RELATIONSHIP_REQUIREMENT_KEY, DEFAULT_BENEFICIARY_RELATIONSHIP_REQUIREMENT),
    [config.systemSettings],
  );

  const isIra = acctType === '60';
  const tierMechanism = isIra ? 'TaxPlnBenfAdd' : 'CustRelAdd (BP / BC)';

  // ── Validation ──────────────────────────────────────────────────────
  const primaryRows = useMemo(() => drafts.filter((b) => b.role === 'primary'), [drafts]);
  const contingentRows = useMemo(() => drafts.filter((b) => b.role === 'contingent'), [drafts]);

  const sumPrimary = useMemo(
    () => primaryRows.reduce((s, b) => s + (b.percentage ?? 0), 0),
    [primaryRows],
  );
  const sumContingent = useMemo(
    () => contingentRows.reduce((s, b) => s + (b.percentage ?? 0), 0),
    [contingentRows],
  );

  const primaryValid = primaryRows.length === 0 || Math.abs(sumPrimary - 100) < 0.01;
  const contingentValid = contingentRows.length === 0 || Math.abs(sumContingent - 100) < 0.01;

  // Required-field gate (BACKLOG-0863): when a requirement is 'required',
  // every draft must satisfy it before Save. noSsn satisfies the SSN rule
  // (Field 92 TINCode='N' path at commit). 'recommended'/'optional' never block.
  const missingSsnNames = useMemo(
    () => (ssnRequirement === 'required'
      ? drafts.filter((b) => !benefSsnSatisfied(b)).map(benefDisplayName)
      : []),
    [drafts, ssnRequirement],
  );
  const missingPhoneNames = useMemo(
    () => (phoneRequirement === 'required'
      ? drafts.filter((b) => !benefPhoneSatisfied(b)).map(benefDisplayName)
      : []),
    [drafts, phoneRequirement],
  );
  // Minor beneficiaries: custodian name + address + phone are required
  // (Bill 2026-09-30, BACKLOG-1648) — the POD form names who the bank pays.
  const missingCustodianNames = useMemo(
    () => drafts.filter((b) => !custodianComplete(b)).map(benefDisplayName),
    [drafts],
  );
  // Trust / Charity beneficiaries have no date of birth (benefDobSatisfied exempts them).
  const missingDobNames = useMemo(
    () => (dobRequirement === 'required'
      ? drafts.filter((b) => !benefDobSatisfied(b)).map(benefDisplayName)
      : []),
    [drafts, dobRequirement],
  );
  const missingRelationshipNames = useMemo(
    () => (relationshipRequirement === 'required'
      ? drafts.filter((b) => !benefRelationshipSatisfied(b)).map(benefDisplayName)
      : []),
    [drafts, relationshipRequirement],
  );
  const requiredFieldsValid = missingSsnNames.length === 0 && missingPhoneNames.length === 0
    && missingDobNames.length === 0 && missingRelationshipNames.length === 0
    && missingCustodianNames.length === 0;

  // Test-identity rules (Bill 2026-09-15): a beneficiary named Test needs a 666
  // SSN/TIN/ATIN (or none), and a 666 number needs the last name Test.
  const identityProblems = useMemo(() => {
    const settings = readTestIdentitySettings(config.systemSettings);
    return drafts
      .map((d) => testIdentityViolation({
        firstName: d.firstName,
        lastName: d.lastName,
        taxId: d.noSsn ? '' : d.taxId,
        tinCode: d.noSsn ? 'N' : 'S',
        cifId: d.cifId,
      }, settings))
      .filter((v): v is string => !!v);
  }, [drafts, config.systemSettings]);

  const canCommit = primaryValid && contingentValid && requiredFieldsValid && identityProblems.length === 0;

  /**
   * Save (CHG-0186, Bill 2026-11-04 option B). Each linked existing customer whose
   * name, date of birth or phone the officer changed goes through Change in
   * Contact first, one at a time, exactly as an owner's edit does. No deferral
   * (Bill 2026-11-04): Discard puts Core's current values back on the row; when
   * Core cannot be compared (or refuses) nothing is saved and the editor stays
   * open with the reason and Try again.
   */
  const save = async (): Promise<void> => {
    if (!changeInContact) { onCommit(drafts); return; }
    const mode = readProfileChangeMode(config.systemSettings);
    const next = [...drafts];
    const notices: ProfileChangeNotice[] = [];
    setSavingCore(true);
    setCoreNotice(null);
    try {
      for (let i = 0; i < next.length; i++) {
        const d = next[i]!;
        if (!benefNeedsCoreCheck(d)) continue;
        const cifId = (d.cifId ?? '').trim();
        // Which Core phone the one phone field stands for, read live; fail-soft to what the row knew.
        let slot: BenefPhoneSlot = d.coreProfile?.phoneSlot ?? 'phoneCell';
        if ((d.phone ?? '').trim()) {
          try {
            const env = await client.accounts.custInq(cifId);
            const inq = parseCustInq((env as { data?: unknown })?.data ?? env, cifId);
            if (inq.success) slot = pickPhoneSlot(inq, d.coreProfile?.linked?.phone);
          } catch { /* the comparison below reports Core being unreachable */ }
        }
        const outcome = await beginProfileChange(benefToProfilePerson(d, slot), {
          source: 'rao-beneficiary-editor',
          cartId: changeInContact.cartId,
          surface: 'cart',
          authorizedByPrefill: changeInContact.authorizedByPrefill ?? '',
        });
        if (!outcome.proceed) {
          setCoreNotice(outcome.notice ?? { tone: 'err', text: 'The beneficiary change was not saved.' });
          setDrafts(next);
          return;
        }
        next[i] = applyCoreOutcome(d, outcome, slot, mode);
        if (outcome.notice) {
          const who = `${d.firstName} ${d.lastName}`.trim();
          notices.push({ ...outcome.notice, text: `Beneficiary ${who} (CIF ${cifId}): ${outcome.notice.text}` });
        }
      }
      setDrafts(next);
      onCommit(next, notices);
    } finally {
      setSavingCore(false);
    }
  };
  const anyMinor = useMemo(() => drafts.some(isMinorBeneficiary), [drafts]);
  const anyNoSsn = useMemo(() => drafts.some((b) => b.noSsn === true), [drafts]);

  // ── Mutators ────────────────────────────────────────────────────────
  const addBlank = (role: BeneficiaryRoleBucket): void => {
    // Auto-distribute percentages across the tier on add (mirrors SPFx +
    // addFromCartPerson). Without autoAllocate the new row stayed at 0%.
    setDrafts((prev) => autoAllocate([
      ...prev,
      {
        id: genId(),
        mechanism: isIra ? 'TaxPlnBenf' : (role === 'primary' ? 'CustRelAdd-BP' : 'CustRelAdd-BC'),
        role,
        firstName: '',
        lastName: '',
        relationship: '',
        percentage: 0,
      },
    ], role));
  };

  const addFromCartPerson = (chip: NonNullable<BeneficiaryEditorModalProps['cartPersonChips']>[number]): void => {
    setDrafts((prev) => {
      const next: BenefDraft[] = [
        ...prev,
        {
          id: genId(),
          mechanism: isIra ? 'TaxPlnBenf' : 'CustRelAdd-BP',
          role: 'primary',
          firstName: chip.firstName ?? '',
          middleName: chip.middleName ?? '',
          lastName: chip.lastName ?? '',
          dob: chip.dob,
          taxId: chip.taxId,
          phone: chip.phone,
          relationship: '',
          percentage: 0,
          cifId: chip.cifId,
        },
      ];
      return autoAllocate(next, 'primary');
    });
  };

  // Reuse a beneficiary entered on another product line — copy its captured
  // fields (name / relationship / dob / taxId / cifId / custodian) into a
  // fresh primary row so the FSR doesn't re-type it (BACKLOG-0837).
  const addFromReusable = (b: BenefDraft): void => {
    setDrafts((prev) => autoAllocate([
      ...prev,
      {
        ...b,
        id: genId(),
        mechanism: isIra ? 'TaxPlnBenf' : 'CustRelAdd-BP',
        role: 'primary',
        percentage: 0,
      },
    ], 'primary'));
  };

  const updateField = <K extends keyof BenefDraft>(id: string, field: K, value: BenefDraft[K]): void => {
    setDrafts((prev) => prev.map((b) => (b.id === id ? { ...b, [field]: value } : b)));
  };

  // Atomic multi-field patch (Bill 2026-07-10, BACKLOG-0847) — the existing-CIF
  // lookup/link fills name + DOB + SSN + phone + cifId in ONE update so a
  // partially-applied link can never exist between renders.
  const patchRow = (id: string, patch: Partial<BenefDraft>): void => {
    setDrafts((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  };

  const removeRow = (id: string): void => {
    setDrafts((prev) => prev.filter((b) => b.id !== id));
  };

  const switchBucket = (id: string): void => {
    setDrafts((prev) => {
      const flipped = prev.map((b) => {
        if (b.id !== id) return b;
        const newRole: BeneficiaryRoleBucket = b.role === 'primary' ? 'contingent' : 'primary';
        const newMech: BenefDraft['mechanism'] = isIra
          ? 'TaxPlnBenf'
          : (newRole === 'primary' ? 'CustRelAdd-BP' : 'CustRelAdd-BC');
        return { ...b, role: newRole, mechanism: newMech };
      });
      // Re-balance BOTH tiers after the move (Bill 2026-07-13 Item 18) — the
      // moved row otherwise keeps its old % and both tiers silently fall off
      // 100. Mirrors what add/remove already do via autoAllocate.
      return autoAllocate(autoAllocate(flipped, 'primary'), 'contingent');
    });
  };

  // Bill 2026-05-08 — per-tier Equal Split. The original UI only exposed
  // a single "Equal Split All" button under Contingent, which Bill flagged
  // during demo as asymmetric (Primary tier had no button). Per-tier buttons
  // are clearer than one global one because the FSR may want to balance
  // Primary while leaving Contingent partially set, or vice versa.
  const equalSplitTier = (role: BeneficiaryRoleBucket): void => {
    setDrafts((prev) => autoAllocate(prev, role));
  };

  if (!open) return null;

  // Filter cart-person chips to only those NOT already added.
  const addedCifs = new Set(drafts.map((b) => b.cifId).filter(Boolean));
  const availableChips = (cartPersonChips ?? []).filter((c) => !addedCifs.has(c.cifId));

  // Reuse-from-another-line chips, minus any already in this line's drafts
  // (match by name + tax id / CIF, same key the parent deduped on).
  const draftKeys = new Set(
    drafts.map((b) => `${`${b.firstName ?? ''} ${b.lastName ?? ''}`.trim().toLowerCase()}|${(b.taxId ?? b.cifId ?? '').toLowerCase()}`),
  );
  const availableReuse = (reusableBeneficiaries ?? []).filter(
    (b) => !draftKeys.has(`${`${b.firstName ?? ''} ${b.lastName ?? ''}`.trim().toLowerCase()}|${(b.taxId ?? b.cifId ?? '').toLowerCase()}`),
  );

  return (
    <>
    <ModalShell
      open={open}
      onClose={onClose}
      title={title ?? 'Beneficiaries'}
      size="xl"
      disableBackdropClose
      confirmClose
      isDirty={drafts !== initial}
      confirmCloseTitle="Discard beneficiary changes?"
      confirmCloseMessage="The beneficiaries you entered here will be lost if you leave now."
      footer={
        <div className="rao-benefedit__footer">
          <span className="rao-benefedit__footer-mech">
            Core wire mechanism: <strong>{tierMechanism}</strong>
          </span>
          <div className="rao-benefedit__footer-actions">
            <Button variant="ghost" onClick={onClose} disabled={savingCore}>Cancel</Button>
            <Button variant="primary" disabled={!canCommit || savingCore} onClick={() => { void save(); }}>
              {comparingProfile ? 'Comparing with Core Director…' : savingCore ? 'Saving…' : 'Save Beneficiaries'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="rao-benefedit">
        <div className="rao-benefedit__cols">
          {/* ── LEFT: editable tiers ─────────────────────────────────── */}
          <div className="rao-benefedit__form">
            {/* Quick-add chip row */}
            {availableChips.length > 0 && (
              <section className="rao-benefedit__section">
                <header className="rao-benefedit__section-h">
                  From this cart
                  <span className="rao-benefedit__section-sub">quick-add a person already on the account</span>
                </header>
                <div className="rao-benefedit__chips">
                  {availableChips.map((chip) => (
                    <button
                      key={chip.id}
                      type="button"
                      className="rao-benefedit__chip"
                      onClick={() => addFromCartPerson(chip)}
                    >
                      + {chip.firstName} {chip.lastName}
                      <span className="rao-benefedit__chip-cif">{chip.cifId ?? '—'}</span>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Reuse a beneficiary entered on another product line (BACKLOG-0837) */}
            {availableReuse.length > 0 && (
              <section className="rao-benefedit__section">
                <header className="rao-benefedit__section-h">
                  From another product
                  <span className="rao-benefedit__section-sub">reuse a beneficiary you already entered</span>
                </header>
                <div className="rao-benefedit__chips">
                  {availableReuse.map((b, i) => (
                    <button
                      key={`${b.id ?? 'reuse'}-${i}`}
                      type="button"
                      className="rao-benefedit__chip"
                      onClick={() => addFromReusable(b)}
                      title={b.custodianName ? `Custodian: ${b.custodianName}` : undefined}
                    >
                      + {b.firstName} {b.lastName}
                      <span className="rao-benefedit__chip-cif">
                        {b.cifId ?? (b.custodianName ? `c/o ${b.custodianName}` : '—')}
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Primary tier */}
            <BenefTierSection
              label="Primary beneficiaries"
              hint="Paid first"
              bucket="primary"
              rows={primaryRows}
              sum={sumPrimary}
              valid={primaryValid}
              ssnRequirement={ssnRequirement}
              phoneRequirement={phoneRequirement}
              dobRequirement={dobRequirement}
              relationshipRequirement={relationshipRequirement}
              onUpdate={updateField}
              onPatchRow={patchRow}
              onSwitch={switchBucket}
              onRemove={removeRow}
              onAdd={() => addBlank('primary')}
              onEqualSplit={() => equalSplitTier('primary')}
            />

            {/* Contingent tier */}
            <BenefTierSection
              label="Contingent beneficiaries"
              hint="Paid only if every primary predeceases"
              bucket="contingent"
              rows={contingentRows}
              sum={sumContingent}
              valid={contingentValid}
              ssnRequirement={ssnRequirement}
              phoneRequirement={phoneRequirement}
              dobRequirement={dobRequirement}
              relationshipRequirement={relationshipRequirement}
              onUpdate={updateField}
              onPatchRow={patchRow}
              onSwitch={switchBucket}
              onRemove={removeRow}
              onAdd={() => addBlank('contingent')}
              onEqualSplit={() => equalSplitTier('contingent')}
            />

            {(!primaryValid || !contingentValid) && (
              <Banner tone="warn" title="Each tier must total 100%">
                Primary: {sumPrimary.toFixed(2)}% · Contingent: {sumContingent.toFixed(2)}%
              </Banner>
            )}

            {/* Required-field gate (BACKLOG-0863) — names the rows blocking Save. */}
            {missingSsnNames.length > 0 && (
              <Banner tone="warn" title="SSN / TIN required for each beneficiary">
                Missing for: {missingSsnNames.join(', ')}. Enter the SSN/TIN, or check
                &ldquo;Beneficiary has no SSN/TIN/ATIN&rdquo; if the beneficiary doesn&rsquo;t have one.
              </Banner>
            )}
            {missingPhoneNames.length > 0 && (
              <Banner tone="warn" title="Phone number required for each beneficiary">
                Missing for: {missingPhoneNames.join(', ')}.
              </Banner>
            )}
            {missingDobNames.length > 0 && (
              <Banner tone="warn" title="Date of birth required for each beneficiary">
                Missing for: {missingDobNames.join(', ')}. Enter it as MM/DD/YYYY. A trust or charity
                beneficiary does not need one.
              </Banner>
            )}
            {missingRelationshipNames.length > 0 && (
              <Banner tone="warn" title="Relationship required for each beneficiary">
                Missing for: {missingRelationshipNames.join(', ')}.
              </Banner>
            )}
            {identityProblems.length > 0 && (
              <Banner tone="err" title="Test customers follow the test-identity rules">
                {identityProblems.join(' ')}
              </Banner>
            )}
            {coreNotice && (
              <Banner tone={coreNotice.tone} title="Change in Contact">
                {coreNotice.text}{' '}
                <Button variant="link" size="sm" onClick={() => { void save(); }} disabled={savingCore || !canCommit}>
                  Try again
                </Button>
              </Banner>
            )}
          </div>

          {/* ── RIGHT: persistent guidance aside ─────────────────────── */}
          <aside className="rao-benefedit__guide" aria-label="Beneficiary guidance">
            <div className="rao-benefedit__guide-body">
              <div>
                <div className="rao-benefedit__guide-h">How this splits</div>
                <ul className="rao-benefedit__guide-ul">
                  <li><strong>Primary</strong> beneficiaries are paid first.</li>
                  <li><strong>Contingent</strong> beneficiaries inherit only if every primary has predeceased.</li>
                  <li>Each tier’s percentages must total exactly <strong>100%</strong>.</li>
                </ul>
              </div>

              <div>
                <div className="rao-benefedit__guide-h">Allocation</div>
                <dl className="rao-benefedit__guide-dl">
                  <dt>Primary</dt>
                  <dd>
                    {primaryRows.length} {primaryRows.length === 1 ? 'person' : 'people'} ·{' '}
                    <span className={primaryValid ? 'rao-benefedit__ok' : 'rao-benefedit__bad'}>
                      {sumPrimary.toFixed(2)}%
                    </span>
                  </dd>
                  <dt>Contingent</dt>
                  <dd>
                    {contingentRows.length} {contingentRows.length === 1 ? 'person' : 'people'} ·{' '}
                    <span className={contingentValid ? 'rao-benefedit__ok' : 'rao-benefedit__bad'}>
                      {sumContingent.toFixed(2)}%
                    </span>
                  </dd>
                </dl>
              </div>

              <div>
                <div className="rao-benefedit__guide-h">Core wire mechanism</div>
                <dl className="rao-benefedit__guide-dl">
                  <dt>Account</dt>
                  <dd>{isIra ? 'IRA (type 60)' : `Deposit POD (type ${acctType})`}</dd>
                  <dt>Operation</dt>
                  <dd><strong>{tierMechanism}</strong></dd>
                </dl>
                <p className="rao-benefedit__guide-note">
                  {isIra
                    ? 'IRA beneficiaries post via TaxPlnBenfAdd on commit.'
                    : 'POD beneficiaries post via CustRelAdd — BP for primary, BC for contingent.'}
                </p>
              </div>

              {/* SSN + phone capture rules (Bill 2026-07-10, BACKLOG-0863) */}
              <div>
                <div className="rao-benefedit__guide-h">SSN &amp; phone</div>
                <ul className="rao-benefedit__guide-ul">
                  <li>
                    <strong>SSN / TIN</strong> is {ssnRequirement} — {ssnRequirement === 'required'
                      ? 'the beneficiary CIF is created at commit and needs it. Online applications often arrive without it; ask the customer and fill it in.'
                      : 'capture it when the customer can provide it (it feeds the beneficiary CIF at commit).'}
                  </li>
                  <li>
                    No SSN/TIN/ATIN? Check the box on the row — the CIF is created
                    on the no-TIN rail (Core Field 92 Tax ID Code = &lsquo;N&rsquo;).
                  </li>
                  <li>
                    <strong>Phone</strong> is {phoneRequirement}
                    {phoneRequirement === 'required' ? '' : ' — capture it when available'}.
                  </li>
                  <li>
                    <strong>Date of birth</strong> is {dobRequirement}
                    {dobRequirement === 'required' ? ' (not needed for a trust or charity)' : ''}.
                    {' '}<strong>Relationship</strong> is {relationshipRequirement}.
                  </li>
                  <li>A linked existing customer&rsquo;s phone and date of birth come from Core.</li>
                  <li>
                    Changing a linked customer&rsquo;s name, date of birth or phone runs
                    <strong> Change in Contact</strong> at Save, the same as for an owner.
                  </li>
                </ul>
              </div>

              {/* Existing-customer lookup + detector (Bill 2026-07-10, BACKLOG-0847) */}
              <div>
                <div className="rao-benefedit__guide-h">Existing customers</div>
                <ul className="rao-benefedit__guide-ul">
                  <li>
                    <strong>Find existing customer</strong> on a row searches Core
                    Director by SSN, name, or CIF number — the same search as the
                    People step.
                  </li>
                  <li>
                    A linked row shows <strong>Existing customer · CIF</strong> and
                    skips creating a new CIF at commit — the account wires to the
                    on-file customer instead.
                  </li>
                  <li>A linked beneficiary needs <strong>no SSN entry</strong> — it&rsquo;s already on file.</li>
                  <li>
                    The editor also watches for likely matches as you type (full
                    SSN, or name + DOB) and suggests them. Nothing links without
                    your click.
                  </li>
                </ul>
              </div>

              {anyNoSsn && (
                <div className="rao-benefedit__guide-alert">
                  <strong>No-TIN beneficiary on this account</strong>
                  At commit the beneficiary CIF is created with Core Director
                  Field 92 Tax ID Code = &lsquo;N&rsquo; (None Required) and an empty Tax ID —
                  Core generates the CIF. Same rail as the NRA/no-TIN customer ruling.
                </div>
              )}

              {anyMinor && (
                <div className="rao-benefedit__guide-alert">
                  <strong>Minor beneficiary — custodian required</strong>
                  A beneficiary under 18 needs a custodian with name, address and
                  phone (email optional). A successor custodian is optional. These
                  print on the POD designation form the owners sign — there is no
                  Core wire field for them.
                  {missingCustodianNames.length > 0 && (
                    <> Still missing for: {missingCustodianNames.join(', ')}.</>
                  )}
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </ModalShell>
    {profileChangeModal}
    </>
  );
}

// ── BenefTierSection — one bucket (primary | contingent) ──────────────

interface BenefTierSectionProps {
  label: string;
  hint: string;
  bucket: BeneficiaryRoleBucket;
  rows: ReadonlyArray<BenefDraft>;
  sum: number;
  valid: boolean;
  /** Admin-configured SSN capture level (BACKLOG-0863). */
  ssnRequirement: BeneficiaryFieldRequirement;
  /** Admin-configured phone capture level (BACKLOG-0863). */
  phoneRequirement: BeneficiaryFieldRequirement;
  /** Admin-configured DOB / relationship capture levels (seed 414). */
  dobRequirement: BeneficiaryFieldRequirement;
  relationshipRequirement: BeneficiaryFieldRequirement;
  onUpdate: <K extends keyof BenefDraft>(id: string, field: K, value: BenefDraft[K]) => void;
  /** Atomic multi-field patch — existing-CIF link/unlink (BACKLOG-0847). */
  onPatchRow: (id: string, patch: Partial<BenefDraft>) => void;
  onSwitch: (id: string) => void;
  onRemove: (id: string) => void;
  onAdd: () => void;
  onEqualSplit: () => void;
}

function BenefTierSection(props: BenefTierSectionProps): JSX.Element {
  const {
    label, hint, bucket, rows, sum, valid, ssnRequirement, phoneRequirement,
    dobRequirement, relationshipRequirement,
    onUpdate, onPatchRow, onSwitch, onRemove, onAdd, onEqualSplit,
  } = props;
  const addLabel = bucket === 'primary' ? '+ Primary beneficiary' : '+ Contingent beneficiary';

  return (
    <section className="rao-benefedit__section">
      <header className="rao-benefedit__section-h">
        {label}
        <span className="rao-benefedit__section-sub">{hint}</span>
        <span className={`rao-benefedit__tier-sum ${valid ? 'is-ok' : 'is-bad'}`}>
          {sum.toFixed(2)}%
        </span>
      </header>

      {rows.length === 0 ? (
        <p className="rao-benefedit__empty">No {bucket} beneficiaries yet.</p>
      ) : (
        rows.map((b) => (
          <BenefRow
            key={b.id}
            draft={b}
            ssnRequirement={ssnRequirement}
            phoneRequirement={phoneRequirement}
            dobRequirement={dobRequirement}
            relationshipRequirement={relationshipRequirement}
            onChange={(field, value) => onUpdate(b.id, field, value)}
            onPatch={(patch) => onPatchRow(b.id, patch)}
            onSwitch={() => onSwitch(b.id)}
            onRemove={() => onRemove(b.id)}
          />
        ))
      )}

      <div className="rao-benefedit__row-actions">
        <button type="button" className="rao-benefedit__add" onClick={onAdd}>
          {addLabel}
        </button>
        <button
          type="button"
          className="rao-benefedit__split"
          onClick={onEqualSplit}
          disabled={rows.length === 0}
          title={`Distribute 100% evenly across all ${bucket} beneficiaries`}
        >
          Equal split
        </button>
      </div>
    </section>
  );
}

// ── BenefRow — single editable beneficiary card ───────────────────────

interface BenefRowProps {
  draft: BenefDraft;
  /** Admin-configured SSN capture level (BACKLOG-0863). */
  ssnRequirement: BeneficiaryFieldRequirement;
  /** Admin-configured phone capture level (BACKLOG-0863). */
  phoneRequirement: BeneficiaryFieldRequirement;
  /** Admin-configured DOB / relationship capture levels (seed 414). */
  dobRequirement: BeneficiaryFieldRequirement;
  relationshipRequirement: BeneficiaryFieldRequirement;
  onChange: <K extends keyof BenefDraft>(field: K, value: BenefDraft[K]) => void;
  /** Atomic multi-field patch — existing-CIF link/unlink (BACKLOG-0847). */
  onPatch: (patch: Partial<BenefDraft>) => void;
  onSwitch: () => void;
  onRemove: () => void;
}

// ── Existing-CIF link helpers (Bill 2026-07-10, BACKLOG-0847) ──────────

// ── Kill-list guard (Bill 2026-07-10) ──────────────────────────────────
// Kill-listed CIFs must never be offered for beneficiary linking. Same
// source GlobalCustomerSearch filters by (client.retail.killList —
// pending-verification + verified CifKill); fetched once per app session
// (module cache); fail-soft to unfiltered on fetch error, matching the
// legacy convention.
let killedCifsCache: Set<string> | null = null;
async function filterKillListed<T extends { cifId: string }>(
  client: ReturnType<typeof useApiClient>,
  rows: T[],
): Promise<T[]> {
  if (!killedCifsCache) {
    const killed = new Set<string>();
    try {
      const [pending, verified] = await Promise.all([
        client.retail.killList
          .list({ status: 'pending-verification', entryType: 'CifKill', top: 500 })
          .catch(() => null),
        client.retail.killList
          .list({ status: 'verified', entryType: 'CifKill', top: 500 })
          .catch(() => null),
      ]);
      for (const page of [pending, verified]) {
        if (!page?.entries) continue;
        for (const e of page.entries) {
          // Test cleanup holds (reason TestAccount) keep the test customer reusable (Bill 2026-10-06).
          if (e.entryType === 'CifKill' && e.targetId && e.reason !== 'TestAccount') killed.add(e.targetId);
        }
      }
    } catch {
      // eslint-disable-next-line no-console
      console.warn('[BeneficiaryEditorModal] kill-list fetch failed — showing unfiltered');
    }
    killedCifsCache = killed;
  }
  return killedCifsCache.size > 0 ? rows.filter((r) => !killedCifsCache!.has(r.cifId)) : rows;
}

/** Debounce for the automatic match detector (ms). */
const DETECTOR_DEBOUNCE_MS = 600;

/**
 * Build the atomic patch that links a Core Director search hit onto a
 * beneficiary row: fills name / DOB / phone / address and pins `cifId`.
 * The tax ID is NOT copied, and one typed for the search is cleared (666 test
 * 2026-11-04): the commit links the existing CIF and skips the CustAdd, so it
 * needs no SSN, and a copied SSN printed in full on the POD form and the
 * owner's customer copy. The SSN input shows "On file with CIF". `noSsn` is
 * force-cleared — a linked existing customer needs no SSN entry (the commit
 * path skips CustAdd entirely), and a stale no-TIN flag must never ride along.
 * Business/Trust hits arrive with only `name` (no first/last) — the whole
 * name lands in lastName, mirroring the commit-side SplitName fallback.
 */
export function buildLinkPatch(row: CustomerSearchRow): Partial<BenefDraft> {
  const patch: Partial<BenefDraft> = { cifId: row.cifId, noSsn: false, taxId: '' };
  if (row.firstName || row.lastName) {
    patch.firstName = row.firstName;
    patch.lastName = row.lastName;
  } else if (row.name) {
    patch.firstName = '';
    patch.lastName = row.name;
  }
  // CustSrch may omit BirthDt; only fill a well-formed ISO date so the
  // <input type="date"> never receives junk (jxDates rule).
  if (row.dob && /^\d{4}-\d{2}-\d{2}/.test(row.dob)) patch.dob = row.dob.slice(0, 10);
  // Digits-only, cap 14 — same normalization as the phone input (BACKLOG-0863).
  if (row.phone) patch.phone = row.phone.replace(/\D/g, '').slice(0, 14);
  // 666 test 2026-11-04: the POD form's Address column ({{address}}) printed blank for a linked
  // beneficiary although the search row carries Core's address. PCB form "POD Beneficiary
  // Designation v21.0" has the Address column; the address the search returned rides along.
  if (row.address && row.address.trim()) patch.address = row.address.trim();
  return patch;
}

/** Label-suffix flag for a requirement level: red "required" (blocks Save),
 *  muted "recommended" (visual nudge, non-blocking), nothing for optional. */
function RequirementFlag({ level }: { level: BeneficiaryFieldRequirement }): JSX.Element | null {
  if (level === 'required') {
    return <span className="rao-benefedit__field-flag">required</span>;
  }
  if (level === 'recommended') {
    return <span className="rao-benefedit__field-flag rao-benefedit__field-flag--soft">recommended</span>;
  }
  return null;
}

function BenefRow({
  draft, ssnRequirement, phoneRequirement, dobRequirement, relationshipRequirement,
  onChange, onPatch, onSwitch, onRemove,
}: BenefRowProps): JSX.Element {
  const minor = isMinorBeneficiary(draft);
  const switchLabel = draft.role === 'primary' ? 'Move to Contingent' : 'Move to Primary';
  const initials = `${(draft.firstName || '?').charAt(0)}${(draft.lastName || '').charAt(0)}`.toUpperCase();
  const noSsn = draft.noSsn === true;
  const linkedCif = (draft.cifId ?? '').trim();
  const ssnMissing = ssnRequirement === 'required' && !benefSsnSatisfied(draft);
  const phoneMissing = phoneRequirement === 'required' && !benefPhoneSatisfied(draft);
  const dobMissing = dobRequirement === 'required' && !benefDobSatisfied(draft);
  const relationshipMissing = relationshipRequirement === 'required' && !benefRelationshipSatisfied(draft);
  const isEntity = benefIsEntity(draft);
  // CHG-0186: a linked trust / charity / business customer's Core-held fields are read-only here.
  const coreLocked = benefCoreLocked(draft);

  // ── Existing-CIF lookup + match detector (Bill 2026-07-10, BACKLOG-0847) ──
  const client = useApiClient();
  const [lookupOpen, setLookupOpen] = useState<boolean>(false);
  const [suggestion, setSuggestion] =
    useState<{ key: string; matches: CustomerSearchRow[] } | null>(null);
  // Detector bookkeeping: one quiet search per distinct identity key
  // (ssn:<9digits> | nd:<name>|<dob>), and a dismissed key never re-prompts.
  const lastRunKeyRef = useRef<string | null>(null);
  const dismissedKeyRef = useRef<string | null>(null);
  const detectAbortRef = useRef<AbortController | null>(null);
  useEffect(() => () => detectAbortRef.current?.abort(), []);

  /**
   * MATCH DETECTOR — automatic, non-blocking. When the row's SSN reaches
   * 9 digits (or first+last name AND DOB are complete while SSN is empty /
   * noSsn is checked), quietly run the SAME CustSrch fan-out the People step
   * uses (runCustomerSearch — SSN→TaxId, name→PersonName; name hits tightened
   * by filterByNameAndDob, exactly like PeopleStep's no-dup-CIF guard).
   * Debounced; fail-soft (a search error can never block editing); NEVER
   * auto-links — it only renders a suggestion the FSR must click.
   */
  useEffect(() => {
    if (linkedCif) { setSuggestion(null); return undefined; }
    const key = benefIdentityKey(draft);
    if (!key) {
      lastRunKeyRef.current = null;
      setSuggestion(null);
      return undefined;
    }
    const useSsn = key.startsWith('ssn:');
    const ssn = useSsn ? key.slice(4) : '';
    const nm = `${draft.firstName ?? ''} ${draft.lastName ?? ''}`.trim();
    if (dismissedKeyRef.current === key || lastRunKeyRef.current === key) return undefined;
    const timer = setTimeout(() => {
      lastRunKeyRef.current = key;
      detectAbortRef.current?.abort();
      const controller = new AbortController();
      detectAbortRef.current = controller;
      void (async () => {
        try {
          const raw = await runCustomerSearch(client, useSsn ? ssn : nm, { signal: controller.signal });
          if (controller.signal.aborted) return;
          const unfiltered = useSsn ? raw : filterByNameAndDob(raw, draft.dob);
          const matches = await filterKillListed(client, unfiltered);
          if (controller.signal.aborted) return;
          setSuggestion(matches.length > 0 ? { key, matches } : null);
          // Record the outcome ON THE DRAFT so the no-SSN gate survives to
          // Save. No match clears this identity for a synthetic CIF; a match
          // revokes any earlier clearance until the FSR links or dismisses.
          onPatch({ noSsnClearedKey: matches.length === 0 ? key : undefined });
        } catch {
          // Fail-soft for EDITING — a search error never blocks typing. But it
          // fails CLOSED for the no-SSN rail: an identity Core could not be
          // searched is not an identity Core confirmed as new, so any earlier
          // clearance is withdrawn and the checkbox goes dark until a search
          // completes. (feedback_gates_must_fail_closed_on_absence.)
          if (!controller.signal.aborted) onPatch({ noSsnClearedKey: undefined });
        }
      })();
    }, DETECTOR_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [client, linkedCif, noSsn, draft.taxId, draft.dob, draft.firstName, draft.lastName, onPatch]);

  /** Link a search hit onto this row (fills fields + pins cifId). */
  const applyMatch = (row: CustomerSearchRow): void => {
    const patch = buildLinkPatch(row);
    // CHG-0186: remember what Core holds, so a later edit is recognised as a Change in Contact.
    onPatch({ ...patch, coreProfile: linkSnapshot({ ...draft, ...patch }) });
    setSuggestion(null);
    setLookupOpen(false);
    lastRunKeyRef.current = null;
    dismissedKeyRef.current = null;
    // CustSrch projections frequently omit BirthDt/phone (Bill 2026-07-13
    // Item 15: linked benef arrived with name+SSN but no DOB/phone). Hydrate
    // the gaps from the full CustInq record, fail-soft — the link stands
    // even when the follow-up inquiry errors.
    if (!patch.dob || !patch.phone || !patch.middleName) {
      void (async () => {
        try {
          const env = await client.accounts.custInq(row.cifId);
          const inq = parseCustInq(
            (env as { data?: unknown })?.data ?? env,
            row.cifId,
          );
          if (!inq.success) return;
          const fill: Partial<BenefDraft> = {};
          if (!patch.dob && /^\d{4}-\d{2}-\d{2}/.test(inq.dob)) {
            fill.dob = inq.dob.slice(0, 10);
          }
          const phone = inq.phoneCell || inq.phoneHome || inq.phoneBus;
          if (!patch.phone && phone) {
            fill.phone = phone.replace(/\D/g, '').slice(0, 14);
          }
          // Middle name — mirror the main person hydrate (useInquiryHydration).
          // Core Director may return "First MI" concatenated in the first-name
          // field, so split it and prefer the explicit F936 middle when present.
          // (Bill 2026-07-22 — the beneficiary editor was silently dropping middle name.)
          if (!patch.middleName) {
            const split = splitFirstMiddle(inq.firstName);
            const middle = inq.middleName || split.middle;
            if (middle) {
              fill.middleName = middle;
              if (split.first && split.first !== inq.firstName) fill.firstName = split.first;
            }
          }
          if (Object.keys(fill).length > 0) {
            onPatch({ ...fill, coreProfile: linkSnapshot({ ...draft, ...patch, ...fill }) });
          }
        } catch {
          /* fail-soft */
        }
      })();
    }
  };

  /** Dismiss the inline suggestion — remembered so the same identity never re-prompts. */
  const dismissSuggestion = (): void => {
    if (suggestion) {
      dismissedKeyRef.current = suggestion.key;
      // The FSR has looked at the match and said it is a different person.
      // That is the affirmative act the one-CIF-per-person rule asks for, so
      // it clears this identity for the no-SSN rail exactly as a no-match does.
      onPatch({ noSsnClearedKey: suggestion.key });
    }
    setSuggestion(null);
  };

  /**
   * Unlink (the ✕ on the pill) — drops ONLY the CIF pin; the filled fields
   * stay. The row reverts to new-CIF creation at commit. The current identity
   * is marked dismissed so the detector doesn't instantly re-suggest the CIF
   * the FSR just unlinked.
   */
  const unlink = (): void => {
    dismissedKeyRef.current = benefIdentityKey(draft) || null;
    lastRunKeyRef.current = null;
    // The address came from the linked customer's Core record; it is not this person's.
    onPatch({ cifId: undefined, coreProfile: undefined, address: undefined });
  };

  const topSuggestion = suggestion?.matches[0];

  return (
    <div className={`rao-benefedit__card ${minor ? 'is-minor' : ''}`}>
      <div className="rao-benefedit__card-head">
        <span className="rao-benefedit__avatar" aria-hidden>{initials}</span>
        <span className="rao-benefedit__card-name">
          {draft.firstName || draft.lastName
            ? `${draft.firstName} ${draft.lastName}`.trim()
            : 'New beneficiary'}
          {/* Existing-customer pill + unlink (Bill 2026-07-10, BACKLOG-0847).
              A pinned cifId means commit SKIPS CustAdd and wires this CIF
              directly (CustRelAdd BP/BC · TaxPlnBenfAdd cif). */}
          {linkedCif && (
            <span
              className="rao-benefedit__cifpill"
              title={`Linked to existing Core Director customer — CIF ${linkedCif}. No new CIF is created at commit.`}
            >
              Existing customer · CIF {linkedCif}
              <button
                type="button"
                className="rao-benefedit__cifpill-x"
                aria-label="Unlink existing customer"
                title="Unlink — this row reverts to creating a new CIF at commit"
                onClick={unlink}
              >
                ✕
              </button>
            </span>
          )}
        </span>
        <span className="rao-benefedit__card-pct">
          <input
            className="rao-benefedit__pct-input"
            type="number"
            min={0}
            max={100}
            step={0.01}
            value={draft.percentage}
            aria-label="Allocation percent"
            onChange={(e) => onChange('percentage', Number(e.target.value))}
          />
          <span className="rao-benefedit__pct-sign">%</span>
        </span>
      </div>

      <div className="rao-benefedit__grid">
        {/* Bill 2026-05-08 — split into two inputs. The prior single-input
            "First Last" pattern called `.trim().split(/\s+/)` on every change,
            which collapsed the trailing space against the React controlled-
            value echo: typing "Test " then "B" would never produce "Test B"
            because the space was thrown away on the prior render and the
            input snapped back to "Test" before the next keystroke. Two
            inputs eliminates the issue and matches PCB form conventions. */}
        <label className="rao-benefedit__field">
          <span className="rao-benefedit__field-h">First name</span>
          <input
            className="rao-benefedit__input"
            value={draft.firstName}
            placeholder="First"
            readOnly={coreLocked}
            title={coreLocked ? BENEF_CORE_LOCKED_NOTE : undefined}
            onChange={(e) => onChange('firstName', e.target.value)}
          />
        </label>
        <label className="rao-benefedit__field">
          <span className="rao-benefedit__field-h">
            Middle <span className="rao-benefedit__field-flag rao-benefedit__field-flag--soft">optional</span>
          </span>
          <input
            className="rao-benefedit__input"
            value={draft.middleName ?? ''}
            placeholder="Middle"
            readOnly={coreLocked}
            title={coreLocked ? BENEF_CORE_LOCKED_NOTE : undefined}
            onChange={(e) => onChange('middleName', e.target.value)}
          />
        </label>
        <label className="rao-benefedit__field">
          <span className="rao-benefedit__field-h">Last name</span>
          <input
            className="rao-benefedit__input"
            value={draft.lastName}
            placeholder="Last"
            readOnly={coreLocked}
            title={coreLocked ? BENEF_CORE_LOCKED_NOTE : undefined}
            onChange={(e) => onChange('lastName', e.target.value)}
          />
          {coreLocked && <span className="rao-benefedit__hint">{BENEF_CORE_LOCKED_NOTE}</span>}
        </label>
        {/* SSN — requirement level is admin-configurable via
            beneficiary.ssn.requirement (default 'required'; Bill 2026-07-10,
            BACKLOG-0863: the beneficiary CIF is created at commit and needs
            it; legacy online-app imports lack it, so the FSR asks the
            customer). The no-SSN checkbox below disables + clears the input
            and satisfies the requirement — commit then CustAdds on the
            Core Director Field 92 TINCode='N' no-TIN rail. */}
        <div className="rao-benefedit__field">
          <label className="rao-benefedit__field">
            <span className="rao-benefedit__field-h">
              SSN / TIN
              <RequirementFlag level={ssnRequirement} />
            </span>
            <input
              className={`rao-benefedit__input ${ssnMissing ? 'is-missing' : ''}`}
              value={noSsn || linkedCif ? '' : (draft.taxId ?? '')}
              placeholder={noSsn
                ? 'No SSN/TIN/ATIN — Field 92 = N'
                // Linked existing customer (BACKLOG-0847): SSN is on file with
                // the CIF — no entry needed (commit skips the CustAdd).
                : linkedCif
                  ? 'On file with CIF — no entry needed'
                  : '000-00-0000'}
              inputMode="numeric"
              disabled={noSsn || !!linkedCif}
              aria-invalid={ssnMissing || undefined}
              /* Strip dashes/space on entry AND paste (Bill 2026-07-13 Item
                 10) — no DOM maxLength, it truncates dashed pastes before
                 the strip can run. 9 digits = SSN/ATIN; EIN pastes also land
                 as 9 digits. */
              onChange={(e) => onChange('taxId', e.target.value.replace(/\D/g, '').slice(0, 9))}
            />
          </label>
          {/* noSsn is MOOT on a linked row (BACKLOG-0847): commit skips the
              CustAdd entirely for an existing CIF, so the no-TIN rail never
              runs — hide the checkbox to avoid a contradictory state. */}
          {!linkedCif && (() => {
            // One CIF per person (Bill 2026-09-23): a synthetic CIF may only be
            // minted for someone Core Director has been searched for and does
            // not know. Until that search completes for THIS name + DOB, the
            // box is dark and says why. Editing the name or DOB afterwards
            // revokes it, because the search no longer describes the person.
            const identityKey = benefIdentityKey({ ...draft, noSsn: true });
            const cleared = benefNoSsnCleared({ ...draft, noSsn: true });
            const hint = cleared
              ? undefined
              : !identityKey
                ? 'Enter first name, last name and date of birth first — Core Director is searched before a record can be created without a tax id.'
                : suggestion
                  ? 'Core Director found a possible match above. Link it, or dismiss it if this is a different person.'
                  : 'Checking Core Director for an existing customer…';
            return (
              <div className="rao-benefedit__nossn-wrap">
                <label className="rao-benefedit__nossn">
                  <input
                    type="checkbox"
                    checked={noSsn}
                    disabled={!cleared}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      // Checking clears the SSN so a stale value can never ride
                      // along with the no-TIN flag into the commit payload.
                      if (checked) onChange('taxId', '');
                      onChange('noSsn', checked);
                    }}
                  />
                  <span>Beneficiary has no SSN/TIN/ATIN</span>
                </label>
                {hint && <div className="rao-benefedit__hint">{hint}</div>}
              </div>
            );
          })()}
        </div>
        <label className="rao-benefedit__field">
          <span className="rao-benefedit__field-h">
            Date of birth
            {!isEntity && <RequirementFlag level={dobRequirement} />}
          </span>
          {/* Typed MM/DD/YYYY (Collette Cruse 2026-10-16: the date control would not
              take number-keypad typing). Reports ISO only when complete. */}
          <DateTextInput
            className={`rao-benefedit__input ${dobMissing ? 'is-missing' : ''}`}
            placeholder={isEntity ? 'Not needed for a trust or charity' : linkedCif ? 'On file with CIF' : 'MM/DD/YYYY'}
            value={draft.dob ?? ''}
            aria-label="Beneficiary date of birth"
            readOnly={coreLocked}
            onChange={(iso) => onChange('dob', iso)}
          />
        </label>
        {/* Phone — new to the modal (BACKLOG-0863); the beneficiary store
            already carried `phone` (BenefDraft / online-app import shape).
            Digits-only, cap 14, matching the PDW ContactSection convention
            (Core Director truncates to its 10-char phone field). Requirement
            level via beneficiary.phone.requirement (default 'recommended'). */}
        <label className="rao-benefedit__field">
          <span className="rao-benefedit__field-h">
            Phone
            <RequirementFlag level={phoneRequirement} />
          </span>
          <input
            className={`rao-benefedit__input ${phoneMissing ? 'is-missing' : ''}`}
            type="tel"
            value={draft.phone ?? ''}
            placeholder="5735551234"
            maxLength={14}
            aria-invalid={phoneMissing || undefined}
            readOnly={coreLocked}
            title={coreLocked ? BENEF_CORE_LOCKED_NOTE : undefined}
            onChange={(e) => onChange('phone', e.target.value.replace(/\D/g, '').slice(0, 14))}
          />
        </label>
        <label className="rao-benefedit__field">
          <span className="rao-benefedit__field-h">
            Relationship
            <RequirementFlag level={relationshipRequirement} />
          </span>
          <select
            className={`rao-benefedit__input ${relationshipMissing ? 'is-missing' : ''}`}
            aria-invalid={relationshipMissing || undefined}
            value={draft.relationship}
            onChange={(e) => onChange('relationship', e.target.value)}
          >
            <option value="">—</option>
            <option value="Spouse">Spouse</option>
            <option value="Child">Child</option>
            <option value="Parent">Parent</option>
            <option value="Sibling">Sibling</option>
            <option value="Trust">Trust</option>
            <option value="Charity">Charity</option>
            <option value="Other">Other</option>
          </select>
        </label>
      </div>

      {/* MATCH-DETECTOR suggestion (Bill 2026-07-10, BACKLOG-0847) —
          non-blocking inline hint; the FSR must click to link. */}
      {topSuggestion && !linkedCif && (
        <div className="rao-benefedit__suggest" role="status">
          <span className="rao-benefedit__suggest-text">
            Possible existing customer:{' '}
            <strong>{topSuggestion.name}</strong>, CIF {topSuggestion.cifId}
            {suggestion && suggestion.matches.length > 1
              ? ` — +${suggestion.matches.length - 1} more via Find existing customer`
              : ''}
          </span>
          <span className="rao-benefedit__suggest-actions">
            <button
              type="button"
              className="rao-benefedit__suggest-use"
              onClick={() => applyMatch(topSuggestion)}
            >
              Use this customer
            </button>
            <button
              type="button"
              className="rao-benefedit__link"
              onClick={dismissSuggestion}
            >
              Dismiss
            </button>
          </span>
        </div>
      )}

      {/* Explicit per-row lookup (Bill 2026-07-10, BACKLOG-0847) — same
          CustSrch fan-out + result-card conventions as the People step. */}
      {lookupOpen && !linkedCif && (
        <BenefCifLookup
          onPick={applyMatch}
          onClose={() => setLookupOpen(false)}
        />
      )}

      {minor && (
        <div className="rao-benefedit__custodian">
          <div className="rao-benefedit__custodian-h">Custodian for this minor beneficiary</div>
          <div className="rao-benefedit__custodian-grid">
            <label className="rao-benefedit__field">
              <span className="rao-benefedit__field-h">
                Custodian full name
                <span className="rao-benefedit__field-flag">required</span>
              </span>
              <input
                className={`rao-benefedit__input ${!(draft.custodianName ?? '').trim() ? 'is-missing' : ''}`}
                value={draft.custodianName ?? ''}
                placeholder="Adult who receives the minor's share"
                onChange={(e) => onChange('custodianName', e.target.value)}
              />
            </label>
            <label className="rao-benefedit__field">
              <span className="rao-benefedit__field-h">
                Custodian phone
                <span className="rao-benefedit__field-flag">required</span>
              </span>
              <input
                className={`rao-benefedit__input ${(draft.custodianPhone ?? '').replace(/\D/g, '').length < 10 ? 'is-missing' : ''}`}
                type="tel"
                value={draft.custodianPhone ?? ''}
                placeholder="5735551234"
                maxLength={14}
                onChange={(e) => onChange('custodianPhone', e.target.value.replace(/\D/g, '').slice(0, 14))}
              />
            </label>
            <label className="rao-benefedit__field rao-benefedit__field--wide">
              <span className="rao-benefedit__field-h">
                Custodian address
                <span className="rao-benefedit__field-flag">required</span>
              </span>
              <input
                className={`rao-benefedit__input ${!(draft.custodianAddress ?? '').trim() ? 'is-missing' : ''}`}
                value={draft.custodianAddress ?? ''}
                placeholder="Street, City, State ZIP"
                onChange={(e) => onChange('custodianAddress', e.target.value)}
              />
            </label>
            <label className="rao-benefedit__field rao-benefedit__field--wide">
              <span className="rao-benefedit__field-h">Custodian email <span className="rao-benefedit__field-flag rao-benefedit__field-flag--soft">optional</span></span>
              <input
                className="rao-benefedit__input"
                type="email"
                value={draft.custodianEmail ?? ''}
                placeholder="name@example.com"
                onChange={(e) => onChange('custodianEmail', e.target.value.trim())}
              />
            </label>
          </div>
          <details className="rao-benefedit__successor" open={!!(draft.successorCustodianName || draft.successorCustodianAddress || draft.successorCustodianPhone)}>
            <summary>Successor custodian (optional) — serves if the custodian cannot</summary>
            <div className="rao-benefedit__custodian-grid">
              <label className="rao-benefedit__field">
                <span className="rao-benefedit__field-h">Successor full name</span>
                <input className="rao-benefedit__input" value={draft.successorCustodianName ?? ''}
                  onChange={(e) => onChange('successorCustodianName', e.target.value)} />
              </label>
              <label className="rao-benefedit__field">
                <span className="rao-benefedit__field-h">Successor phone</span>
                <input className="rao-benefedit__input" type="tel" value={draft.successorCustodianPhone ?? ''} placeholder="5735551234" maxLength={14}
                  onChange={(e) => onChange('successorCustodianPhone', e.target.value.replace(/\D/g, '').slice(0, 14))} />
              </label>
              <label className="rao-benefedit__field rao-benefedit__field--wide">
                <span className="rao-benefedit__field-h">Successor address</span>
                <input className="rao-benefedit__input" value={draft.successorCustodianAddress ?? ''} placeholder="Street, City, State ZIP"
                  onChange={(e) => onChange('successorCustodianAddress', e.target.value)} />
              </label>
              <label className="rao-benefedit__field rao-benefedit__field--wide">
                <span className="rao-benefedit__field-h">Successor email</span>
                <input className="rao-benefedit__input" type="email" value={draft.successorCustodianEmail ?? ''} placeholder="name@example.com"
                  onChange={(e) => onChange('successorCustodianEmail', e.target.value.trim())} />
              </label>
            </div>
          </details>
          <p className="rao-benefedit__custodian-note">
            Printed on the POD designation form the owners sign (Missouri Transfers to Minors Law) — no Core wire field.
          </p>
        </div>
      )}

      <div className="rao-benefedit__card-bar">
        {/* Existing-CIF lookup toggle (Bill 2026-07-10, BACKLOG-0847) — hidden
            once linked (the pill's ✕ is the way back). */}
        {!linkedCif && (
          <button
            type="button"
            className="rao-benefedit__link rao-benefedit__link--lead"
            onClick={() => setLookupOpen((v) => !v)}
          >
            {lookupOpen ? 'Close customer search' : 'Find existing customer'}
          </button>
        )}
        <button type="button" className="rao-benefedit__link" onClick={onSwitch}>
          {switchLabel}
        </button>
        <button
          type="button"
          className="rao-benefedit__link rao-benefedit__link--danger"
          onClick={onRemove}
        >
          Remove
        </button>
      </div>
    </div>
  );
}

// ── BenefCifLookup — per-row "Find existing customer" (BACKLOG-0847) ───
//
// Reuses the People step's search stack end-to-end: runCustomerSearch is the
// single source of the CustSrch wire shape (SSN→TaxId, full-name→PersonName,
// single-token fan-out, CIF→CustId, jXRL cold-start retry, dedupe + sort).
// Manual-trigger only (Enter / Search click) — CustSrch is expensive and Core
// Director rate-limits aggressive searching (usePersonSearch port note).
// Result cards follow the GlobalCustomerSearch conventions: initials avatar,
// name + custType pill, FULL SSN (banker tool — never masked, per
// feedback_ssn_masking), phone, address, CIF right-aligned.

interface BenefCifLookupProps {
  /** Link the picked customer onto the row (fills fields + pins cifId). */
  onPick: (row: CustomerSearchRow) => void;
  onClose: () => void;
}

function BenefCifLookup({ onPick, onClose }: BenefCifLookupProps): JSX.Element {
  const client = useApiClient();
  const [query, setQuery] = useState<string>('');
  const [rows, setRows] = useState<ReadonlyArray<CustomerSearchRow>>([]);
  const [searching, setSearching] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState<boolean>(false);
  const controllerRef = useRef<AbortController | null>(null);
  useEffect(() => () => controllerRef.current?.abort(), []);

  const runLookup = async (): Promise<void> => {
    const q = query.trim();
    if (q.length < 2) return;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setSearching(true);
    setError(null);
    try {
      const merged = await runCustomerSearch(client, q, { signal: controller.signal });
      if (controller.signal.aborted) return;
      const visible = await filterKillListed(client, merged);
      if (controller.signal.aborted) return;
      setRows(visible);
      setSearched(true);
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Search failed');
      setRows([]);
    } finally {
      if (!controller.signal.aborted) setSearching(false);
    }
  };

  return (
    <div className="rao-benefedit__lookup">
      <div className="rao-benefedit__lookup-bar">
        <label className="rao-benefedit__field" style={{ flex: 1 }}>
          <span className="rao-benefedit__field-h">Search by SSN, name, or CIF number</span>
          <input
            className="rao-benefedit__input"
            value={query}
            placeholder="000-00-0000, Smith, John Smith, or CIF #"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void runLookup();
              }
            }}
          />
        </label>
        <button
          type="button"
          className="rao-benefedit__lookup-btn"
          disabled={searching || query.trim().length < 2}
          onClick={() => void runLookup()}
        >
          {searching ? 'Searching…' : 'Search'}
        </button>
        <button type="button" className="rao-benefedit__link" onClick={onClose}>
          Close
        </button>
      </div>

      {error && (
        <div className="rao-benefedit__lookup-err" role="alert">{error}</div>
      )}

      {!error && searched && rows.length === 0 && !searching && (
        <div className="rao-benefedit__lookup-empty">No matching customers found.</div>
      )}

      {rows.length > 0 && (
        <div className="rao-benefedit__lookup-results">
          {rows.map((r) => {
            const ini = `${(r.firstName || r.name || '?').charAt(0)}${(r.lastName || '').charAt(0)}`.toUpperCase();
            return (
              <button
                key={r.cifId}
                type="button"
                className="rao-benefedit__lookup-row"
                title={`Link ${r.name} (CIF ${r.cifId}) to this beneficiary`}
                onClick={() => onPick(r)}
              >
                <span className="rao-benefedit__lookup-avatar" aria-hidden>{ini}</span>
                <span className="rao-benefedit__lookup-main">
                  <span className="rao-benefedit__lookup-name">
                    {r.name}
                    <span className="rao-benefedit__lookup-type">{r.custType || 'Customer'}</span>
                  </span>
                  <span className="rao-benefedit__lookup-meta">
                    {r.taxId && <span title="SSN/TIN">{formatSsn(r.taxId)}</span>}
                    {r.phone && <span title="Phone">{r.phone}</span>}
                    {r.address && <span title={r.address} className="rao-benefedit__lookup-addr">{r.address}</span>}
                  </span>
                </span>
                <span className="rao-benefedit__lookup-cif">CIF {r.cifId}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
